import React, { useState, useEffect, useRef } from "react";
import { Text, AppState } from "react-native";
import { NavigationContainer, DefaultTheme } from "@react-navigation/native";
import { createStackNavigator } from "@react-navigation/stack";
import { SafeAreaProvider } from "react-native-safe-area-context";
import { Locales } from "../../utils/locale";
import OnboardingFlow from "../OnboardingFlow";
import Root from "../Root";
import ModalScreen from "../ModalScreen";
import PinUnlock from "../PinUnlock";
import SafeMode from "../SafeMode";
import Inactive from "../Inactive";
import { useStorage } from "../../utils/storage";
import { Context as PinContext, STORAGE_KEY_STORED_PIN } from "../../utils/pin";
import {
    STORAGE_KEY_EXTERNAL_RESOURCES,
    STORAGE_KEY_EXTERNAL_RESOURCES_ZIP,
} from "../../utils/resources";
import { Context as MeContext } from "../../utils/me";
import {
    LogicStateContext,
    defaultLogicState,
    LogicState,
    STORAGE_KEY_LOGIC_STATE,
} from "../../utils/quiz";
import { useAuth } from "../../utils/firebase";
import { useFurniture } from "../../utils/contentful";
import {
    Audience,
    AudienceContext,
    FurnitureContext,
    STORAGE_KEY_STORED_AUDIENCE,
} from "../../utils/furniture";
import { AppContextProvider } from "./../../components/AppContextProvider";
import {
    PlayfairDisplay_800ExtraBold,
    PlayfairDisplay_600SemiBold,
    PlayfairDisplay_700Bold_Italic,
    useFonts as usePlayFairFonts,
} from "@expo-google-fonts/playfair-display";

import {
    MavenPro_500Medium,
    MavenPro_400Regular,
    MavenPro_700Bold,
    useFonts as useMavenFonts,
} from "@expo-google-fonts/maven-pro";

import {
    SpaceMono_400Regular,
    SpaceMono_700Bold_Italic,
    useFonts as useSpaceFonts,
} from "@expo-google-fonts/space-mono";
import { GestureHandlerRootView } from "react-native-gesture-handler";

declare const global: { HermesInternal: null | {} };

const Stack = createStackNavigator();

const navTheme = {
    ...DefaultTheme,
    colors: {
        ...DefaultTheme.colors,
        background: "transparent",
    },
};

const renderLogin = () => {
    return (
        <Stack.Navigator screenOptions={{ headerShown: false }}>
            <Stack.Screen name="Login" component={OnboardingFlow} />
        </Stack.Navigator>
    );
};

const renderRoot = me => {
    return (
        <MeContext.Provider value={me}>
            <Stack.Navigator
                screenOptions={{
                    headerShown: false,
                    presentation: "modal",
                    animationEnabled: true,
                    cardStyle: {
                        flex: 1,
                    },
                }}
                initialRouteName="Root">
                <Stack.Screen name="Root" component={Root} />
                <Stack.Screen name="ModalScreen" component={ModalScreen} />
                <Stack.Screen
                    name="Safe Mode"
                    component={SafeMode}
                    initialParams={{ returnRoute: "Root" }}
                />
            </Stack.Navigator>
        </MeContext.Provider>
    );
};

const renderUnlock = () => {
    return (
        <Stack.Navigator
            screenOptions={{ headerShown: false, presentation: "modal" }}
            initialRouteName="PinUnlock">
            <Stack.Screen name="PinUnlock" component={PinUnlock} />
            <Stack.Screen
                name="Safe Mode"
                component={SafeMode}
                initialParams={{ returnRoute: "PinUnlock" }}
            />
        </Stack.Navigator>
    );
};

const renderInactive = () => <Inactive />;

const App = () => {
    const auth = useAuth();
    let [playfairFontsLoaded] = usePlayFairFonts({
        PlayfairDisplay_800ExtraBold,
        PlayfairDisplay_600SemiBold,
        PlayfairDisplay_700Bold_Italic,
    });

    let [mavenFontsLoaded] = useMavenFonts({
        MavenPro_700Bold,
        MavenPro_500Medium,
        MavenPro_400Regular,
    });

    let [spaceFontsLoaded] = useSpaceFonts({
        SpaceMono_400Regular,
        SpaceMono_700Bold_Italic,
    });

    const fontsLoaded =
        spaceFontsLoaded && mavenFontsLoaded && playfairFontsLoaded;
    const [me, setMe] = useState(null);
    const [storedPin, , clearStoredPin] = useStorage(STORAGE_KEY_STORED_PIN);
    const [storedAudience, setStoredAudience, clearStoredAudience] = useStorage(
        STORAGE_KEY_STORED_AUDIENCE,
    );
    const [, , clearStoredResourceZip] = useStorage(
        STORAGE_KEY_EXTERNAL_RESOURCES_ZIP,
    );
    const [, , clearStoredResourceCategories] = useStorage(
        STORAGE_KEY_EXTERNAL_RESOURCES,
    );
    const [{ data: furniture }] = useFurniture(Locales.EN_US, Audience.Teen);
    const [activePin, setActivePin] = useState();
    const [isInitializing, setIsInitializing] = useState(true);
    const [currentAppStateActive, setCurrentAppStateActive] = useState(true);
    const [_audience, setActiveAudience] = useState(null);
    const [_logicState, setLogicState] = useState<LogicState>(null);
    const [_storedLogicState, setStoredLogicState, clearStoredLogicState] =
        useStorage(STORAGE_KEY_LOGIC_STATE);
    const currentAppState = useRef(AppState.currentState);
    let content;

    const setAudience = (audience: Audience) => {
        setActiveAudience(audience);
        setStoredAudience(audience);
    };

    const handleAppState = nextAppState => {
        if (nextAppState.match(/inactive|background/)) {
            currentAppState.current = nextAppState;
            setCurrentAppStateActive(!currentAppState.current);
            setActivePin(null);
        }

        if (nextAppState === "active") {
            currentAppState.current = nextAppState;
            setCurrentAppStateActive(!!currentAppState.current);
        }
    };

    useEffect(() => {
        const unsubscribeFn = auth().onAuthStateChanged(user => {
            if (isInitializing) {
                setIsInitializing(false);
            }

            // Make it ez to find my records in Cloud Firestore
            console.log("Current user ID", user?.uid);

            if (!user) {
                clearStoredPin();
                clearStoredResourceZip();
                clearStoredResourceCategories();
                setActivePin(null);
                clearStoredAudience();
                setActiveAudience(Audience.Teen);
                clearStoredLogicState();
            }

            setMe(user);
        });

        return unsubscribeFn;
    }, []);

    useEffect(() => {
        const appStateChangeListener = AppState.addEventListener(
            "change",
            handleAppState,
        );
        return () => appStateChangeListener.remove();
    }, []);

    // if a stored audience is set, set it as active
    useEffect(() => {
        if (!_audience) {
            setActiveAudience(storedAudience || Audience.Teen);
        }
    }, []);

    useEffect(() => {
        if (!_logicState) {
            setLogicState(_storedLogicState || defaultLogicState);
        }
    }, []);

    if (!fontsLoaded) return null;

    if (isInitializing || !furniture) {
        return <Text>Initializing...</Text>;
    }

    if (me && storedPin) {
        if (activePin) {
            content = renderRoot(me);
        } else {
            content = renderUnlock();
        }
    } else {
        auth().signOut();
        content = renderLogin();
    }

    return (
        <SafeAreaProvider>
            <AppContextProvider>
                <LogicStateContext.Provider
                    value={{
                        logicState: _logicState,
                        setLogicState: logicState => setLogicState(logicState),
                        storeLogicState: logicState =>
                            setStoredLogicState(logicState),
                    }}>
                    <AudienceContext.Provider
                        value={{
                            audience: _audience,
                            setAudience,
                        }}>
                        <FurnitureContext.Provider value={furniture}>
                            <PinContext.Provider
                                value={[activePin, setActivePin]}>
                                <GestureHandlerRootView style={{ flex: 1 }}>
                                    <NavigationContainer
                                        theme={navTheme}
                                        documentTitle={{
                                            enabled: false,
                                        }}>
                                        {currentAppStateActive && content}
                                        {!currentAppStateActive &&
                                            renderInactive()}
                                    </NavigationContainer>
                                </GestureHandlerRootView>
                            </PinContext.Provider>
                        </FurnitureContext.Provider>
                    </AudienceContext.Provider>
                </LogicStateContext.Provider>
            </AppContextProvider>
        </SafeAreaProvider>
    );
};

export default App;
